import { default as React, useEffect, useState } from 'react';
import { Button } from '@material-ui/core';
import PeopleIcon from '@material-ui/icons/People';
import { ManageOwnersModal } from './ManageOwnersModal';
import { Entity, stringifyEntityRef } from '@backstage/catalog-model';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';

interface ManageOwnersButtonProps {
  entity: Entity;
  currentUserEmail: string;
  isAuthorized: boolean;
}

export const ManageOwnersButton = ({
  entity,
  currentUserEmail,
  isAuthorized,
}: ManageOwnersButtonProps) => {
  const catalogApi = useApi(catalogApiRef);
  const [modalOpen, setModalOpen] = useState(false);
  // Optimistic local owners state: set after a successful save so the UI
  // reflects the change immediately, before the catalog entity is refreshed.
  const [localOwners, setLocalOwners] = useState<string[] | null>(null);
  const reloadTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );

  // Clean up the reload timer on unmount
  useEffect(() => {
    return () => {
      if (reloadTimerRef.current) {
        clearTimeout(reloadTimerRef.current);
      }
    };
  }, []);

  // Parse current owners from annotation
  const ownersAnnotation =
    entity.metadata.annotations?.['microapp/owners'] || '';
  const createdBy = entity.metadata.annotations?.['microapp/createdBy'] || '';

  let entityOwners: string[];
  if (ownersAnnotation) {
    entityOwners = ownersAnnotation.split(',').map(email => email.trim());
  } else if (createdBy) {
    entityOwners = [createdBy];
  } else {
    entityOwners = [];
  }

  // Once the entity annotation catches up with the optimistic update, clear
  // the local override so we go back to reading from the entity directly.
  useEffect(() => {
    if (localOwners !== null) {
      const entitySet = [...entityOwners].sort().join(',');
      const localSet = [...localOwners].sort().join(',');
      if (entitySet === localSet) {
        setLocalOwners(null);
      }
    }
  }, [ownersAnnotation]); // eslint-disable-line react-hooks/exhaustive-deps

  // Use optimistic state when available, otherwise use entity annotation
  const currentOwners = localOwners ?? entityOwners;

  const handleOpenModal = () => {
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
  };

  const handleOwnersUpdated = async (newOwners: string[]) => {
    setLocalOwners(newOwners);

    // Trigger a catalog entity refresh so the About card and entity page
    // pick up the updated owners annotation without a manual sync click.
    try {
      const entityRef = stringifyEntityRef(entity);
      await catalogApi.refreshEntity(entityRef);
      // Give the catalog a moment to re-process, then reload so all cards
      // on the entity page reflect the updated annotation.
      reloadTimerRef.current = setTimeout(() => window.location.reload(), 500);
    } catch {
      // Optimistic state already updated the UI; a manual sync will still work.
    }
  };

  // Only show button if user is authorized (owner)
  if (!isAuthorized) {
    return null;
  }

  return (
    <>
      <Button
        variant="outlined"
        size="medium"
        startIcon={<PeopleIcon />}
        onClick={handleOpenModal}
        fullWidth
      >
        Manage Owners
      </Button>

      <ManageOwnersModal
        open={modalOpen}
        onClose={handleCloseModal}
        appName={entity.metadata.name}
        entityKind={entity.kind}
        entityNamespace={entity.metadata.namespace || 'default'}
        entityName={entity.metadata.name}
        currentOwners={currentOwners}
        currentUserEmail={currentUserEmail}
        createdBy={createdBy}
        onOwnersUpdated={handleOwnersUpdated}
      />
    </>
  );
};
